import {inject, Injectable} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {HttpClient, HttpContext} from '@angular/common/http';

import {WorkflowDefinitionControllerService} from '../api/api/workflowDefinitionController.service';
import {WorkflowInstanceControllerService} from '../api/api/workflowInstanceController.service';
import {WorkflowRegistryControllerService} from '../api/api/workflowRegistryController.service';
import {ModuleFunctionControllerService} from '../api/api/moduleFunctionController.service';
import {WorkflowDefinition} from '../api/model/workflowDefinition';
import {ModuleManifest} from '../api/model/moduleManifest';
import {WorkflowInstanceDto} from '../api/model/workflowInstanceDto';
import {CreateWorkflowRequest} from '../api/model/createWorkflowRequest';
import {Manifest} from '../api/model/manifest';
import {PromptOptimizationRequest} from '../api/model/promptOptimizationRequest';
import {TriggerWorkflowRequest} from '../api/model/triggerWorkflowRequest';
import {DeploymentInfo} from '../api/model/deploymentInfo';
import {SUPPRESS_ERROR_HANDLING} from '../interceptors/http-error.interceptor';

import {
  buildEdgesFromManifest,
  FlowEdge,
  getTriggerLabel,
  RuleStepDescription
} from '../components/workflow-editor/workflow.models';
import {SseService} from './sse/sse.service';
import {StateConfigService} from './state/state-config.service';

/** Ein aufgelöstes Freigabe-Ziel (Organisation oder Nutzer). */
export interface MyCheckShareTargetInfo {
  id: string;
  scope: 'GLOBAL' | 'ORGANISATION' | 'USER';
  targetId: number | null;
  label: string;
}

/** Freigabe in der Admin-Sicht. */
export interface MyCheckShare {
  shareId: string;
  /** Die admin-eigene Kopie – Ziel des Admin-Editors. */
  definitionId: string;
  /** Herkunft; `null`, wenn das Original inzwischen gelöscht wurde. */
  sourceDefinitionId: string | null;
  sourceProjectId: number | null;
  name: string;
  description: string | null;
  version: string;
  /** Anzahl der verwendeten Prüfregelsets – trägt den Hinweistext im Teilen-Dialog. */
  ruleCount: number;
  /** Status der Kopie; `DRAFT` heißt unvollständig und für Empfänger nicht ausführbar. */
  status: string;
  global: boolean;
  /** Gezielte Ziele; die globale Freigabe steckt in `global`. */
  targets: MyCheckShareTargetInfo[];
  /** Was durch das Teilen zentral sichtbar wurde bzw. entfernt werden musste. */
  warnings: string[];
  /** Offener Entwurf der Kopie (Admin-Builder); `null`, wenn keiner offen ist. */
  draftId?: string | null;
  /** Katalog-Eintrag, aus dem die Kopie installiert bzw. dem sie zugeordnet ist. */
  catalogKey?: string | null;
  /** Installierte Katalog-Version; `0` = zugeordnet, noch nicht aus dem Katalog installiert. */
  catalogVersion?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

// Angelegt, geändert und aufgehoben werden Freigaben seit Runde 2 des Katalogs nur noch über
// Katalog-Freigaben im Admin Center. Der Adminbereich liest sie und bearbeitet Entwürfe.

/**
 * Ein mir zentral bereitgestellter myCheck.
 *
 * <p>Trägt bewusst **kein Manifest** und keine Quell-IDs: bearbeitet wird die Kopie nur im
 * Adminbereich. `definitionId` ist nötig zum Ausführen, `shareId` zum Duplizieren.
 */
export interface SharedMyCheck {
  shareId: string;
  /**
   * Definitions-ID der admin-eigenen Kopie. Nötig zum **Ausführen** — unbedenklich, sie gehört
   * keinem Kundenprojekt. Die Quelldefinition des fremden Projekts steht hier bewusst nicht.
   */
  definitionId: string;
  name: string;
  description: string | null;
  version: string;
  ruleCount: number;
  /** Status der Kopie; `DRAFT` heißt unvollständig gespeichert und damit nicht ausführbar. */
  status: string;
  sharedAt?: string;
  /**
   * Katalog-Schlüssel und -Version der Freigabe (stage-übergreifender Katalog). Fehlen bei
   * Freigaben, die noch nicht über den Katalog installiert bzw. übernommen wurden.
   */
  catalogKey?: string | null;
  catalogVersion?: number | null;
}

/**
 * Ein Projekt, in dem ein zwischen Projekten geteilter myCheck sichtbar ist (Live-Verlinkung,
 * nicht zu verwechseln mit der zentralen Bereitstellung oben). Trägt die Liste „A, B (Eigentümer), C".
 */
export interface SharedProject {
  projectId: number;
  name: string;
  owner: boolean;
}

/** Ein Projekt der Organisation im Teilen-Dialog. */
export interface ProjectShareTarget {
  projectId: number;
  name: string;
  /** Der myCheck ist bereits in dieses Projekt geteilt. */
  linked: boolean;
  /**
   * Die Teilen-Regel ist erfüllt (Org-Admin + in beiden Projekten ≥ Bearbeiter, oder
   * Org-Bearbeiter + in beiden Projekten Admin). Entscheidet das Backend – das Frontend kennt keine
   * Rollennamen. Gilt für Teilen, Aufheben und Eigentümer ändern.
   */
  allowed: boolean;
  /**
   * Dasselbe für „aus allen Projekten löschen". Bei Custom Roles kann es abweichen: dort zählt
   * „myCheck löschen" statt „myCheck verwalten".
   */
  deleteAllowed: boolean;
  /**
   * Titel der verwendeten Dokumentenklassen, die es in diesem Projekt weder als Standardklasse noch
   * mit gleichem Titel gibt. Dort entfällt die Einschränkung auf diese Klassen.
   */
  missingDocumentClasses: string[];
}

/** Ein myCheck (Prüfregelset), das ein Prüflauf verwendet – für die Rückfrage beim Teilen. */
export interface ReferencedRuleset {
  id: string;
  name: string;
  kind: 'KI' | 'IDS' | 'HYBRID' | 'CROSS';
  ownerProjectId: number;
  ownerProjectName: string | null;
  /** Gehört demselben Projekt wie der Prüflauf und kann mitgeteilt werden. */
  shareable: boolean;
  /** Projekte, mit denen es bereits geteilt ist. */
  linkedProjectIds: number[];
}

/** Log entry from backend SSE events */
export interface WorkflowLogEntry {
  timestamp: string;
  level: string;
  message: string;
  moduleId?: string;
  phase?: string;
  details?: Record<string, unknown>;
  /** Backward compatibility with earlier backend payloads. */
  context?: Record<string, unknown>;
}

/** KI-Token-Verbrauch eines Workflow-Schritts. */
export interface AiUsage {
  model?: string;
  modelDeployment?: string;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
}

/**
 * Fortschritt eines Teillaufs: ein Sub-Run je Regelset ({@code multi_check_executor}) bzw. je
 * Regel ({@code HYBRID_RULESET_EXECUTOR}).
 *
 * Ersetzt die frühere Notlösung, den Fortschritt als {@code moduleId = "…:2/3"} zu kodieren –
 * damit lässt sich Status und Fortschritt pro Regelset zuordnen statt aus einem String zu parsen.
 */
export interface WorkflowSubRunInfo {
  rulesetId?: string;
  rulesetName?: string;
  /** IDS | KI | HYBRID */
  rulesetType?: string;
  /** 1-basierter Index des laufenden Teillaufs. */
  index?: number;
  /** Gesamtzahl der Teilläufe. */
  total?: number;
  childInstanceId?: string;
}

/**
 * Fortschritt eines Prüflaufs in **Einzelprüfungen** – eine Einheit ist eine Datei × eine Prüfregel.
 *
 * Kommt aus `GET /workflows/instances/{id}/progress` und (identisch) im SSE-Event `PROGRESS`.
 * `unitsTotal === null` bedeutet "kein Einheiten-Plan vorhanden" (Altläufe, Läufe ohne planbare
 * Einheiten) – dann bleibt die Anzeige beim schrittbasierten Fortschritt.
 */
export interface CheckProgress {
  instanceId: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'PAUSED';
  currentNodeId?: string;
  unitsTotal?: number | null;
  unitsDone?: number;
  unitsFailed?: number;
  startedAt?: string;
  completedAt?: string;
  /**
   * Zweite, feinere Skala: Elemente *innerhalb* der gerade laufenden Prüfregel (Räume aus einem
   * Raumbuch, LV-Positionen, Kapitel). `null`/undefined, solange keine Regel elementweise
   * auffächert – dann wird die Zeile schlicht nicht gerendert.
   *
   * Bewusst nicht in `unitsTotal` eingerechnet: Eine Hybrid-Regel mit 40 Räumen ist im
   * Einheitenplan genau *eine* Einheit. Die Zahl ist regelbezogen und beginnt bei jeder Regel neu.
   */
  elementUnitsTotal?: number | null;
  elementUnitsDone?: number | null;
  /** Anzeigename der Element-Ebene ("Positionen", "Kapitel", "Räume", "Elemente") – kommt aus dem Backend. */
  elementLabel?: string | null;
}

/**
 * Eine Zeile des Ereignisprotokolls aus `GET /workflows/instances/{id}/log`.
 *
 * Bewusst OHNE Nutzdaten: Die Protokoll-Ansicht lud früher `GET /workflows/instances/{id}` (alle
 * StepLogs samt `inputData` und `outputData`) plus je Prüfregel ein `/children`. Bei einem Fan-out
 * über hunderte Datei-Regel-Paare waren das mehrere Megabyte, in denen der Prüfbericht mehrfach
 * steckte – der Browser fror beim Rendern ein. Details kommen über `getInstanceLogStepDetail()`,
 * erst beim Aufklappen einer Zeile.
 */
export interface RunLogEntry {
  /** StepLog-ID – der Schlüssel für den Detail-Abruf. */
  id: string;
  /** Instanz, zu der dieser Schritt gehört; Schlüssel in `RunLogPage.scopes`. */
  instanceId: string;
  nodeId?: string;
  moduleId?: string;
  status?: 'PENDING' | 'RUNNING' | 'AWAITING_INPUT' | 'COMPLETED' | 'FAILED' | 'SKIPPED';
  startedAt?: string;
  /** Frühestmögliche Bearbeitung bei gedrosseltem Fan-out; sonst undefined. */
  scheduledFor?: string;
  completedAt?: string;
  statusMessage?: string;
  errorMessage?: string;
  statusMessageTruncated?: boolean;
  errorMessageTruncated?: boolean;
  /** Ob es Details zum Aufklappen gibt – vom Backend entschieden, nicht clientseitig geprüft. */
  hasDetails?: boolean;
  aiTotalTokens?: number;
  durationMs?: number;
  ruleName?: string;
  fileName?: string;
}

/** Teillauf-Kontext einer Instanz im Laufbaum (Prüfregel bzw. Element). */
export interface RunScope {
  instanceId: string;
  parentInstanceId?: string;
  parentNodeId?: string;
  definitionId?: string;
  /** Anzeigename: Prüfregel-Name, sonst "Element N", sonst der Definitionsname. */
  label?: string;
  /** 0 = Wurzel, 1 = Teillauf/Prüfregel, 2 = Element. */
  depth?: number;
  status?: string;
  fanoutIndex?: number;
  ruleId?: string;
  ruleName?: string;
  fileId?: string;
  fileName?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface RunLogPage {
  rootInstanceId: string;
  entries: RunLogEntry[];
  /** `instanceId` → Teillauf-Metadaten. Unpaginiert, begrenzt durch Prüfregeln × Dateien. */
  scopes: { [instanceId: string]: RunScope };
  total: number;
  page: number;
  size: number;
  hasMore: boolean;
  /** Laufbaum musste über die Eltern-Kette aufgelöst werden (Sub-Runs von vor V27). */
  legacyTreeWalk?: boolean;
}

/**
 * Nutzdaten einer einzelnen Protokollzeile aus `GET /workflows/instances/{id}/log/{stepLogId}`.
 *
 * Überschreitet ein Blob das serverseitige Limit, ist `inputData`/`outputData` `null` und der Text
 * steht gekappt in `inputPreview`/`outputPreview`. Abgeschnittenes JSON wird bewusst nicht als JSON
 * geliefert.
 */
export interface StepLogDetail {
  id: string;
  instanceId?: string;
  nodeId?: string;
  moduleId?: string;
  status?: string;
  correlationId?: string;
  startedAt?: string;
  scheduledFor?: string;
  completedAt?: string;
  durationMs?: number;
  ruleId?: string;
  ruleName?: string;
  fileId?: string;
  fileName?: string;
  statusMessage?: string;
  errorMessage?: string;
  inputData?: { [key: string]: any } | null;
  inputPreview?: string;
  inputTruncated?: boolean;
  inputChars?: number;
  outputData?: { [key: string]: any } | null;
  outputPreview?: string;
  outputTruncated?: boolean;
  outputChars?: number;
  /** Modul-Logs, separat gekappt (max. 500 Einträge). */
  logs?: any[];
  logsTruncated?: boolean;
  aiModel?: string;
  aiModelDeployment?: string;
  aiInputTokens?: number;
  aiOutputTokens?: number;
  aiTotalTokens?: number;
}

/**
 * Woher eine Prüfdauer stammt. Geht bis in die UI, damit "gemessen" und "inklusive Wartezeit" nicht
 * als dieselbe Zahl erscheinen.
 */
export type DurationSource =
  | 'MODULE_REPORTED'
  | 'STEP_CLOCK'
  | 'INSTANCE_CLOCK'
  | 'MIXED'
  | 'NONE';

/**
 * Kumulierte Prüfdauer einer Prüfregel bzw. einer Datei.
 *
 * `durationMs === null` heißt "nicht messbar", nie 0. `id` ist `null`, wo nur ein Name verfügbar ist
 * (IDS – der Modul-Output trägt dort keine Spezifikations-ID).
 */
export interface DurationBucket {
  id?: string | null;
  name?: string | null;
  durationMs?: number | null;
  /** Zahl der Messpunkte, über die kumuliert wurde – Grundlage für den Durchschnitt. */
  sampleCount: number;
  /** Schritte ohne verwertbare Dauer. `> 0` heißt: die Summe ist unvollständig. */
  missingCount: number;
  /** COMBINED-Prüfmodus: die Regel lief über die Dateimenge, eine Aufteilung je Datei gibt es nicht. */
  combined?: boolean;
  source?: DurationSource;
}

/**
 * Antwort von `GET /workflows/instances/{id}/durations`.
 *
 * Ersetzt die frühere clientseitige Berechnung, die nur `instance.steps` auswerten konnte und damit
 * für HYBRID und gemischte Läufe strukturell nichts finden konnte: dort liegen die tragenden
 * Schritte in Kind- bzw. Enkel-Instanzen.
 */
export interface CheckDurations {
  rootInstanceId: string;
  /** Gesamtdauer des Laufs; bei Altläufen die einzige belastbare Zahl. */
  totalDurationMs?: number | null;
  rules: DurationBucket[];
  files: DurationBucket[];
  source?: DurationSource;
}

/** SSE Event types for workflow execution */
export interface WorkflowExecutionEvent {
  type: 'AWAITING_INPUT' | 'STEP_RUNNING' | 'STEP_COMPLETED' | 'COMPLETED' | 'FAILED' | 'PROGRESS';
  instanceId: string;
  nodeId?: string;
  moduleId?: string;
  prompt?: { type: string; config: any };
  outputData?: any;
  error?: string;
  /** Nur bei STEP_COMPLETED: Status-Nachricht des Moduls. */
  statusMessage?: string;
  /** Nur bei STEP_COMPLETED: Dauer in Millisekunden. */
  durationMs?: number;
  /** Log-Einträge die mit diesem Event gesendet werden. */
  logs?: WorkflowLogEntry[];
  /** Nur bei STEP_COMPLETED: KI-Token-Verbrauch dieses Schritts, falls vorhanden. */
  aiUsage?: AiUsage;
  /** Nur bei Sub-Run-Fortschritt (ein Teillauf je Regelset bzw. je Hybrid-Regel). */
  subRun?: WorkflowSubRunInfo;
  /** Nur bei `PROGRESS`: Fortschritt in Einzelprüfungen. */
  progress?: CheckProgress;
}

/** AI Model deployment info from Azure AI Foundry — re-exported from the generated client for existing consumers. */
export {DeploymentInfo};

@Injectable({providedIn: 'root'})
export class WorkflowService {
  private definitionApi = inject(WorkflowDefinitionControllerService);
  private instanceApi = inject(WorkflowInstanceControllerService);
  private registryApi = inject(WorkflowRegistryControllerService);
  private moduleFunctionApi = inject(ModuleFunctionControllerService);
  private sseService = inject(SseService);
  private stateConfig = inject(StateConfigService);
  private http = inject(HttpClient);

  /** Base path resolved from the generated definition API client, consistent with every other method here. */
  private get basePath(): string {
    return (this.definitionApi as any).basePath ?? '';
  }

  private readonly definitionUpdatedSubject = new Subject<void>();
  /** Emittiert nach jedem erfolgreichen updateDefinition()-Aufruf. */
  readonly definitionUpdated$ = this.definitionUpdatedSubject.asObservable();

  // ─── Modules ───────────────────────────────────────────────────────────────

  /** Load all available modules from registry — returns backend ModuleManifest[] directly. */
  listModules(): Observable<ModuleManifest[]> {
    return this.registryApi.listModules() as Observable<ModuleManifest[]>;
  }

  /** List module categories */
  listCategories(): Observable<string[]> {
    return this.registryApi.getCategories().pipe(
      catchError(() => of([] as string[])),
    );
  }

  // ─── AI Models (Foundry) ──────────────────────────────────────────────────

  /**
   * Load available AI model deployments from Foundry via backend proxy.
   * Uses a manual HttpClient call (not the generated FoundryModelsControllerService.listModels())
   * because SUPPRESS_ERROR_HANDLING must be set — callers render their own inline error state.
   */
  listAiModels(): Observable<DeploymentInfo[]> {
    // Das Backend filtert auf die für den Aufrufer freigeschalteten Modelle. Der
    // Organisationskontext muss deshalb mit: ohne ihn gilt die persönliche FeatureConfig, und ein
    // Enterprise-Kunde sähe in seiner Organisation nur das Standardmodell.
    const organisationId = this.stateConfig.getOrganisationIdOrUndefined();
    const url = `${this.basePath}/workflows/models?capability=chat_completion`
      + (organisationId ? `&organisationId=${organisationId}` : '');
    return this.http.get<DeploymentInfo[]>(url, {
      context: new HttpContext().set(SUPPRESS_ERROR_HANDLING, true),
    });
  }

  optimizePrompt(prompt: string, options?: { context?: string; systemPrompt?: string }): Observable<string> {
    const requestBody: PromptOptimizationRequest = {
      prompt,
      context: options?.context,
      systemPrompt: options?.systemPrompt,
    };
    return this.moduleFunctionApi.promptOptimization(requestBody).pipe(
      map((response) => {
        if (typeof response?.value !== 'string') {
          throw new Error('Ungültige Prompt-Optimierungsantwort vom Backend');
        }
        return response.value;
      }),
    );
  }

  /**
   * Interpret a rule's steps AND possible results into short, human-readable German sentences
   * (one per item, in order) via the backend AI endpoint. Suppresses global error handling —
   * callers use this best-effort.
   */
  interpretRule(steps: RuleStepDescription[], results: RuleStepDescription[]): Observable<{ steps: string[]; results: string[] }> {
    return this.http.post<{ steps: string[]; results: string[] }>(
      `${this.basePath}/workflows/functions/interpret_rule`,
      {steps, results},
      {
        withCredentials: true,
        context: new HttpContext().set(SUPPRESS_ERROR_HANDLING, true),
      },
    ).pipe(map(response => ({
      steps: Array.isArray(response?.steps) ? response.steps : [],
      results: Array.isArray(response?.results) ? response.results : [],
    })));
  }

  // ─── Workflow Definitions ──────────────────────────────────────────────────

  /** Load workflow definitions for the overview page — only WORKFLOW type, no hybrid rulesets. */
  listDefinitions(projectId?: number, includeArchived = false): Observable<{
    prebuilt: WorkflowDefinition[];
    custom: WorkflowDefinition[]
  }> {
    return (this.definitionApi.listDefinitions(projectId, 'WORKFLOW', includeArchived, 'body') as Observable<WorkflowDefinition[]>).pipe(
      map((defs) => {
        const prebuilt = defs.filter(d => d.projectId == null);
        const custom = defs.filter(d => d.projectId != null);
        return {prebuilt, custom};
      }),
      catchError(() => of({
        prebuilt: [] as WorkflowDefinition[],
        custom: [] as WorkflowDefinition[],
      })),
    );
  }

  /**
   * Load a single workflow definition by its database primary key `id`.
   */
  getDefinition(id: string, projectId?: number): Observable<WorkflowDefinition> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.definitionApi.getDefinition(id, pid) as Observable<WorkflowDefinition>;
  }

  /** Parse manifestJson from a WorkflowDefinition into a Manifest object */
  parseManifest(def: WorkflowDefinition): Manifest {
    if (def.manifestJson) {
      try {
        return JSON.parse(def.manifestJson);
      } catch { /* ignore parse errors */
      }
    }
    return this.createEmptyManifest();
  }

  /** Save a manifest as a new workflow definition */
  saveDefinition(manifest: Manifest, projectId: number, definitionType?: 'WORKFLOW' | 'HYBRID_RULESET' | 'HYBRID_RULE'): Observable<WorkflowDefinition> {
    const request: CreateWorkflowRequest = {manifest, projectId, definitionType: definitionType ?? 'WORKFLOW'};
    return this.definitionApi.createDefinition(request, projectId) as Observable<WorkflowDefinition>;
  }

  /** Update an existing workflow definition */
  updateDefinition(id: string, manifest: Manifest, projectId: number): Observable<WorkflowDefinition> {
    const request: CreateWorkflowRequest = {manifest, projectId};
    return (this.definitionApi.updateDefinition(request, projectId, id) as Observable<WorkflowDefinition>).pipe(
      tap(() => this.definitionUpdatedSubject.next()),
    );
  }

  /**
   * Direktstart eines Prüfregelsets: legt den automatisch erzeugten, für den Nutzer unsichtbaren
   * myCheck des Sets an – oder aktualisiert ihn, wenn er schon existiert.
   *
   * <p>Idempotent über (Projekt, Regelset): der Aufrufer schickt bei JEDEM Direktstart ein frisch
   * gebautes Default-Manifest, damit spätere Änderungen am Set (KI-Prompts, Ausgabeparameter →
   * Berichtsspalten) nicht veralten. Das Backend erkennt die bestehende Definition und überschreibt
   * ihr Manifest, statt eine zweite anzulegen.
   *
   * <p>Bewusst über den rohen HttpClient statt den generierten Client: `auto-for-ruleset` ist neu und
   * taucht erst nach `./api-client-generation.sh` in `src/app/api` auf (generiert + gitignored).
   */
  upsertAutoDefinitionForRuleset(rulesetId: string, manifest: Manifest, projectId: number): Observable<WorkflowDefinition> {
    const request: CreateWorkflowRequest = {manifest, projectId};
    const url = `${this.basePath}/workflows/definitions/auto-for-ruleset`
      + `?projectId=${projectId}&rulesetId=${encodeURIComponent(rulesetId)}`;
    return this.http.put<WorkflowDefinition>(url, request).pipe(
      tap(() => this.definitionUpdatedSubject.next()),
    );
  }

  /**
   * Archiviert eine Workflow-Definition (myCheck oder Hybrid-Regelset/-Regel).
   * Löscht bewusst nicht hart – bereits ausgeführte Definitionen dürfen ihre Berichte nicht
   * verlieren; siehe WorkflowDefinitionService#archive im Backend.
   */
  archiveDefinition(id: string, projectId?: number): Observable<{ [key: string]: string }> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.definitionApi.deleteDefinition(id, pid) as Observable<{ [key: string]: string }>;
  }

  /** Stellt eine archivierte Definition wieder her (Status → INACTIVE). */
  restoreDefinition(id: string, projectId?: number): Observable<WorkflowDefinition> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.definitionApi.restoreDefinition(id, pid) as Observable<WorkflowDefinition>;
  }

  /** Activate a workflow definition */
  activateDefinition(id: string, projectId?: number): Observable<WorkflowDefinition> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.definitionApi.activateDefinition(id, pid) as Observable<WorkflowDefinition>;
  }

  /** Deactivate a workflow definition */
  deactivateDefinition(id: string, projectId?: number): Observable<WorkflowDefinition> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.definitionApi.deactivateDefinition(id, pid) as Observable<WorkflowDefinition>;
  }

  /** Validate a workflow manifest */
  validateManifest(manifest: Manifest, projectId: number): Observable<{ [key: string]: any }> {
    const request: CreateWorkflowRequest = {manifest, projectId};
    return this.definitionApi.validateManifest(request, projectId) as Observable<{ [key: string]: any }>;
  }

  // ─── Workflow Instances ────────────────────────────────────────────────────

  /**
   * Trigger a workflow.
   *
   * @param reportName Optionaler, frei vergebener Berichtsname für DIESEN Lauf. Ohne Angabe bleibt
   *   es beim bisherigen Verhalten: der Bericht trägt den Namen der myCheck-Definition.
   */
  triggerWorkflow(definitionId: string, triggerData?: {
    [key: string]: any
  }, projectId?: number, reportName?: string): Observable<WorkflowInstanceDto> {
    // Der Typ wird lokal um `reportName` erweitert, statt sich auf das generierte Modell zu
    // verlassen: `src/app/api` ist gitignored und wird im Build neu erzeugt, das Feld steht dort
    // also erst, wenn das Backend im jeweiligen Spec-Environment deployt ist. So baut das Frontend
    // vorher und nachher, und ein Backend ohne das Feld ignoriert es einfach.
    const request: TriggerWorkflowRequest & { reportName?: string } = {definitionId, triggerData};
    if (reportName) request.reportName = reportName;
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.instanceApi.triggerWorkflow(request, pid) as Observable<WorkflowInstanceDto>;
  }

  /** Get a workflow instance */
  getInstance(id: string, projectId?: number): Observable<WorkflowInstanceDto> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.instanceApi.getInstance(id, pid) as Observable<WorkflowInstanceDto>;
  }

  /**
   * Fortschritt eines laufenden Prüflaufs in Einzelprüfungen.
   *
   * Bewusst ein manueller HttpClient-Aufruf statt des generierten Clients: Der Endpunkt ist neu und
   * steht im generierten `api/`-Ordner erst nach dem nächsten `./api-client-generation.sh`-Lauf zur
   * Verfügung – so ist das Feature nicht an eine Client-Regenerierung gekoppelt. Fehler werden
   * unterdrückt, weil der Aufrufer im Sekundentakt pollt und einen Ausfall selbst überbrückt
   * (globales Error-Handling würde bei jedem Tick ein Toast zeigen).
   */
  getInstanceProgress(instanceId: string, projectId?: number): Observable<CheckProgress> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    const url = `${this.basePath}/workflows/instances/${encodeURIComponent(instanceId)}/progress?projectId=${pid}`;
    return this.http.get<CheckProgress>(url, {
      context: new HttpContext().set(SUPPRESS_ERROR_HANDLING, true),
    });
  }

  /**
   * Ereignisprotokoll eines Prüflaufs, seitenweise und ohne Nutzdaten.
   *
   * Wie `getInstanceProgress()` ein manueller HttpClient-Aufruf: Der Endpunkt ist neu und steht im
   * generierten `api/`-Ordner erst nach dem nächsten `./api-client-generation.sh`-Lauf zur Verfügung.
   *
   * Fehler werden hier NICHT unterdrückt – anders als beim Fortschritts-Poll ist das der Inhalt der
   * Ansicht, ein stiller Ausfall wäre ein leeres Protokoll ohne Erklärung.
   */
  getInstanceLog(instanceId: string, page = 0, size = 200, projectId?: number): Observable<RunLogPage> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    const url = `${this.basePath}/workflows/instances/${encodeURIComponent(instanceId)}/log`
      + `?projectId=${pid}&page=${page}&size=${size}`;
    return this.http.get<RunLogPage>(url);
  }

  /**
   * Nutzdaten einer einzelnen Protokollzeile – erst beim Aufklappen abrufen.
   *
   * Genau das war die Ursache des Einfrierens: Vorher hing der komplette Modul-Output an jedem
   * Protokolleintrag, obwohl im Regelfall keiner aufgeklappt wird.
   */
  getInstanceLogStepDetail(instanceId: string, stepLogId: string, projectId?: number): Observable<StepLogDetail> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    const url = `${this.basePath}/workflows/instances/${encodeURIComponent(instanceId)}`
      + `/log/${encodeURIComponent(stepLogId)}?projectId=${pid}`;
    return this.http.get<StepLogDetail>(url);
  }

  /**
   * Prüfdauer je Prüfregel und je geprüfter Datei, serverseitig über den gesamten Laufbaum
   * aggregiert.
   *
   * Fehler werden unterdrückt: Die Dauer ist eine Zusatzinformation neben dem Protokoll. Fällt sie
   * aus, zeigt die Ansicht "–" statt die ganze Seite scheitern zu lassen.
   */
  getInstanceDurations(instanceId: string, projectId?: number): Observable<CheckDurations | null> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    const url = `${this.basePath}/workflows/instances/${encodeURIComponent(instanceId)}/durations?projectId=${pid}`;
    return this.http.get<CheckDurations>(url, {
      context: new HttpContext().set(SUPPRESS_ERROR_HANDLING, true),
    }).pipe(catchError(() => of(null)));
  }

  /** Get child instances (sub-workflows) of a parent instance */
  getChildInstances(parentId: string, projectId?: number): Observable<WorkflowInstanceDto[]> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return (this.instanceApi.getChildInstances(parentId, pid) as Observable<WorkflowInstanceDto[]>).pipe(
      catchError(() => of([])),
    );
  }

  /** List workflow instances */
  listInstances(definitionId?: string, projectId?: number): Observable<WorkflowInstanceDto[]> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return (this.instanceApi.listInstances(pid, definitionId) as Observable<WorkflowInstanceDto[]>).pipe(
      catchError(() => of([])),
    );
  }

  /** Cancel a running workflow instance */
  cancelInstance(id: string, projectId?: number): Observable<{ [key: string]: string }> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.instanceApi.cancelInstance(id, pid) as Observable<{ [key: string]: string }>;
  }

  /**
   * Bucht die MyCheck-Credits beim Start eines Prüflaufs ab (Reservierung).
   * Wirft HTTP 402, wenn kein Kontingent mehr vorhanden ist. Idempotent pro Instanz.
   * Fehler werden bewusst nicht global behandelt, damit der Aufrufer den 402-Fall selbst erkennt.
   */
  reserveMyCheckCredits(instanceId: string, projectId?: number): Observable<void> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    const url = `${this.basePath}/workflows/check-results/reserve?projectId=${pid}&workflowInstanceId=${encodeURIComponent(instanceId)}`;
    return this.http.post<void>(url, {}, {
      context: new HttpContext().set(SUPPRESS_ERROR_HANDLING, true),
    });
  }

  /**
   * Erstattet die beim Start abgebuchten MyCheck-Credits zurück – nur bei technischem
   * Fehlschlag. Die Prüfung (Status FAILED, Reservierung vorhanden, noch nicht erstattet)
   * erfolgt idempotent im Backend; ein Abbruch (CANCELLED) und Erfolg (COMPLETED) werden dort abgelehnt.
   */
  refundMyCheckCredits(instanceId: string, projectId?: number): Observable<void> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    const url = `${this.basePath}/workflows/check-results/refund?projectId=${pid}&workflowInstanceId=${encodeURIComponent(instanceId)}`;
    return this.http.post<void>(url, {}, {
      context: new HttpContext().set(SUPPRESS_ERROR_HANDLING, true),
    });
  }

  // ─── Edges ─────────────────────────────────────────────────────────────────

  /** Build edges from a manifest (delegates to utility) */
  buildEdges(manifest: Manifest, modules?: ModuleManifest[]): FlowEdge[] {
    return buildEdgesFromManifest(manifest, modules);
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /** Derive trigger label from a WorkflowDefinition's manifestJson */
  getTriggerLabelFromDef(def: WorkflowDefinition): string {
    if (!def.manifestJson) return '';
    try {
      const manifest: Manifest = JSON.parse(def.manifestJson);
      return getTriggerLabel(manifest.trigger?.moduleId);
    } catch {
      return '';
    }
  }

  /** Derive module count from a WorkflowDefinition's manifestJson */
  getModuleCountFromDef(def: WorkflowDefinition): number {
    if (!def.manifestJson) return 0;
    try {
      const manifest: Manifest = JSON.parse(def.manifestJson);
      return (manifest.steps?.length ?? 0) + 1; // +1 for trigger
    } catch {
      return 0;
    }
  }

  /** Create an empty manifest template for new workflows */
  createEmptyManifest(): Manifest {
    return {
      name: 'Neuer Workflow',
      version: 'v1',
      description: '',
      trigger: {
        nodeId: 'trigger_0',
        moduleId: 'manual_trigger',
        moduleVersion: undefined,
        config: {},
        onSuccess: undefined,
      },
      steps: [],
    };
  }

  // ─── Workflow Execution (Interactive) ────────────────────────────────────────

  /** Subscribe to SSE events for a running workflow instance. */
  subscribeToExecutionEvents(instanceId: string, projectId?: number): Observable<WorkflowExecutionEvent> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.sseService.listen<WorkflowExecutionEvent>(
      `/workflows/instances/${instanceId}/events?projectId=${pid}`
    ).pipe(
      map(event => event as unknown as WorkflowExecutionEvent)
    );
  }

  /** Resume a paused workflow with user input. */
  resumeWorkflow(instanceId: string, userInput: any, projectId?: number): Observable<any> {
    const pid = projectId ?? this.stateConfig.getProjectId();
    return this.instanceApi.resumeExecution(userInput, pid, instanceId);
  }

  /** Load all available workflow definitions for a project (PUBLIC + project-owned). */
  listAvailableDefinitions(projectId: number): Observable<WorkflowDefinition[]> {
    return (this.definitionApi.listAvailableDefinitions(projectId, 'body') as Observable<WorkflowDefinition[]>).pipe(
      catchError(() => of([] as WorkflowDefinition[])),
    );
  }

  /** Resolve a workflow definition by its unique slug (e.g. 'ids-check'). */
  getDefinitionBySlug(slug: string): Observable<WorkflowDefinition> {
    return this.definitionApi.getDefinitionBySlug(slug, 'body') as Observable<WorkflowDefinition>;
  }

  // ─── Geteilte myChecks ───────────────────────────────────────────────────────
  //
  // Handgeschriebene HttpClient-Aufrufe, wie im globalen Abschnitt von ruleset.service.ts:
  // src/app/api ist generiert und gitignored, und die Neugenerierung braucht ein laufendes
  // Backend (./api-client-generation.sh). Sobald die OpenAPI-Spec die Endpunkte enthält, können
  // diese Aufrufe auf den generierten WorkflowDefinitionControllerService umgestellt werden.

  private get sharesPath(): string {
    return `${this.basePath}/workflows/definitions/shares`;
  }

  /** Admin: alle aktiven Freigaben. */
  listShares(): Observable<MyCheckShare[]> {
    return this.http.get<MyCheckShare[]>(this.sharesPath);
  }

  /**
   * Admin: die Kopie einer Freigabe zum Bearbeiten laden.
   *
   * <p>Eigener Endpunkt, weil `GET workflows/definitions/{id}` einen `projectId`-Pflichtparameter
   * hat — im Adminbereich gibt es kein Projekt. Das ist der einzige wirklich projektlose Lesezugriff
   * auf eine Definition; `getDefinition(id)` ohne projectId fällt auf `getProjectId()` zurück und
   * schickt damit einen veralteten oder `0`-Wert mit.
   */
  getSharedDefinition(shareId: string): Observable<WorkflowDefinition> {
    return this.http.get<WorkflowDefinition>(`${this.sharesPath}/${shareId}/definition`);
  }

  /**
   * Admin: den offenen Entwurf der Kopie laden (projektlos, status=DRAFT, `publishedId` = Kopie).
   * Ohne Entwurf antwortet das Backend mit 404 – das ist der Normalfall und Sache des Aufrufers.
   */
  getSharedDraft(shareId: string): Observable<WorkflowDefinition> {
    return this.http.get<WorkflowDefinition>(`${this.sharesPath}/${shareId}/draft`);
  }

  /**
   * Admin: Entwurf der Kopie speichern – legt ihn beim ersten Speichern an. Die Antwort ist der
   * Entwurf; `manifest.isValid` trägt die Vollständigkeit.
   */
  updateSharedDraft(shareId: string, manifest: Manifest): Observable<WorkflowDefinition> {
    return this.http.put<WorkflowDefinition>(
      `${this.sharesPath}/${shareId}/draft`, {manifest, definitionType: 'WORKFLOW'});
  }

  /** Admin: Entwurf der Kopie verwerfen. Die installierte Fassung bleibt unverändert. */
  discardSharedDraft(shareId: string): Observable<void> {
    return this.http.delete<void>(`${this.sharesPath}/${shareId}/draft`);
  }

  /** Empfänger: die mir freigegebenen myChecks. */
  listSharedForMe(projectId: number): Observable<SharedMyCheck[]> {
    return this.http.get<SharedMyCheck[]>(`${this.basePath}/workflows/definitions/shared`, {
      params: {projectId},
    }).pipe(catchError(() => of([] as SharedMyCheck[])));
  }

  /** Empfänger: eine Freigabe als eigenen, bearbeitbaren myCheck übernehmen. */
  duplicateFromShare(shareId: string, projectId: number): Observable<WorkflowDefinition> {
    return this.http.post<WorkflowDefinition>(
      `${this.basePath}/workflows/definitions/shared/${shareId}/duplicate`, null, {params: {projectId}});
  }

  /** Eigenen myCheck duplizieren – ersetzt das frühere clientseitige Klonen des Manifests. */
  /**
   * @param copyRulesetIds nur bei einem aus einem anderen Projekt geteilten Prüflauf: hier nicht
   *                       verfügbare myChecks, die als eigene Kopien mitkommen sollen. Alle übrigen
   *                       nicht verfügbaren entfernt das Backend aus der Kopie.
   */
  duplicateDefinition(id: string, projectId: number, copyRulesetIds: string[] = []): Observable<WorkflowDefinition> {
    return this.http.post<WorkflowDefinition>(
      `${this.basePath}/workflows/definitions/${id}/duplicate`, {copyRulesetIds}, {params: {projectId}});
  }

  /** myChecks eines hierher geteilten Prüflaufs, die in diesem Projekt nicht verfügbar sind. */
  listMissingRulesetsForDuplicate(id: string, projectId: number): Observable<ReferencedRuleset[]> {
    return this.http.get<ReferencedRuleset[]>(
      `${this.basePath}/workflows/definitions/${id}/duplicate/missing-rulesets`, {params: {projectId}});
  }

  // ─── myChecks zwischen Projekten teilen (normale Nutzer) ─────────────────────
  //
  // Live-Verlinkung: eine Definition, Eigentümer ist ihr projectId. `projectId` ist bei allen drei
  // Aufrufen das Projekt, aus dem heraus gearbeitet wird.

  private projectSharesPath(definitionId: string): string {
    return `${this.basePath}/workflows/definitions/${definitionId}/project-shares`;
  }

  /** Mögliche Zielprojekte für den Teilen-Dialog; `projectId` ist das Eigentümerprojekt. */
  listProjectShareTargets(definitionId: string, projectId: number): Observable<ProjectShareTarget[]> {
    return this.http.get<ProjectShareTarget[]>(`${this.projectSharesPath(definitionId)}/targets`, {
      params: {projectId},
    });
  }

  /**
   * Prüflauf in weitere Projekte teilen. Liefert alle Projekte, in denen er danach sichtbar ist.
   *
   * @param rulesetIds myChecks des Prüflaufs, die mit denselben Projekten mitgeteilt werden sollen
   */
  shareWithProjects(definitionId: string, projectId: number, projectIds: number[],
                    rulesetIds: string[] = []): Observable<SharedProject[]> {
    return this.http.post<SharedProject[]>(this.projectSharesPath(definitionId), {projectIds, rulesetIds}, {
      params: {projectId},
    });
  }

  /** Die myChecks, die ein Prüflauf verwendet, mit Freigabe-Stand; `projectId` ist das Eigentümerprojekt. */
  listReferencedRulesetsForShare(definitionId: string, projectId: number): Observable<ReferencedRuleset[]> {
    return this.http.get<ReferencedRuleset[]>(`${this.projectSharesPath(definitionId)}/rulesets`, {
      params: {projectId},
    });
  }

  /** Ein Projekt, mit dem geteilt ist, zum Eigentümer machen; `projectId` ist der bisherige. */
  transferOwnership(definitionId: string, projectId: number, newOwnerProjectId: number,
                    keepCurrentOwner: boolean): Observable<SharedProject[]> {
    return this.http.post<SharedProject[]>(`${this.projectSharesPath(definitionId)}/owner`,
      {newOwnerProjectId, keepCurrentOwner}, {params: {projectId}});
  }

  /**
   * Prüflauf „aus allen Projekten löschen": archiviert ihn und hebt vorher alle Freigaben auf – nur
   * mit der Teilen-Regel für jedes verlinkte Projekt.
   */
  archiveDefinitionEverywhere(id: string, projectId: number): Observable<{ [key: string]: string }> {
    return this.http.delete<{ [key: string]: string }>(`${this.basePath}/workflows/definitions/${id}`, {
      params: {projectId, removeShares: 'true'},
    });
  }

  /** Freigabe für ein Projekt aufheben – aus dem Eigentümer- oder aus dem Zielprojekt selbst. */
  unshareFromProject(definitionId: string, targetProjectId: number, projectId: number): Observable<SharedProject[]> {
    return this.http.delete<SharedProject[]>(`${this.projectSharesPath(definitionId)}/${targetProjectId}`, {
      params: {projectId},
    });
  }

  /**
   * @deprecated Use getDefinitionBySlug('ids-check') instead — seeding now happens at startup.
   */
  seedIdsCheckDefinition(): Observable<WorkflowDefinition> {
    return this.getDefinitionBySlug('ids-check');
  }
}
